import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, SubmitHandler } from "react-hook-form"
import { InputTextField } from '../FormFields/RenderInputTextField';
import { Vessel } from '../../types/Vessel';
import { SelectField } from '../FormFields/RenderSelectField';
import GetVesselOptions from '../FormHelpers/VesselSelectOptions';
import { getContacts } from "../../data/contacts";
import { SelectMultipleField } from '../FormFields/RenderSelectMultipleField';
import GetContactOptions from '../FormHelpers/ContactSelectOptions';
import { CheckboxField } from "../FormFields/RenderCheckboxField";
import { createVessel, removeVessel, updateVessel } from "../../data/vessels";
import { hideModal } from "../../data/modal";
import VesselWeightDraughtTable from "../Forms/VesselWeightDraughtTable";
import VesselDraughtKGMax from "./VesselDraughtKgMaxTable";

import {
    Button,
    Divider,
    Grid,
    Typography
} from "@mui/material";

import {
    Close as CloseIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { NumberField } from "../FormFields/RenderNumberField";

const VesselForm = ({ vessel, vessels }) => {
    const dispatch = useDispatch();

    const selectedData = useSelector((state) => ({
        contacts: state.contacts,
    }));
    
    useEffect(() => {
        dispatch(getContacts());
    }, [dispatch]);
    
    const { contacts } = selectedData;

    const { handleSubmit, control, formState, register } = useForm({
        defaultValues: {
            emptyCenterOfGravity: { x: 0, y: 0, z: 1 },
            ...(vessel ?? new Vessel()),
        },
    });
    const { isSubmitting } = formState;

    const onSubmit: SubmitHandler<Vessel> = (data) => {
        if (vessel) {
            dispatch(updateVessel(data));
        } else {
            dispatch(createVessel(data));
        }  
    }

    return (
        <form onSubmit={ handleSubmit(onSubmit) }>
            <Grid container xs={12}>
                <Grid item xs={12} sm={12}>
                    <Grid item xs={12} className='form-fields-container'>
                        <Grid xs={12} sm={6}>
                            <InputTextField
                                name='name'
                                label='Name'
                                control={ control }
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <InputTextField
                                name='mmsi'
                                label='MMSI'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <InputTextField
                                name='euid'
                                label='EUID'
                                control={ control }
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className='form-fields-container'>
                        <Grid item xs={12} sm={3}>
                            <NumberField
                                name='maxTeu'
                                label='Max TEU'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <NumberField
                                name='maxWeight'
                                label='Max Weight(kg)'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <NumberField
                                name='emptyWeightInTonnes'
                                label='Empty Weight (T)'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <NumberField
                                name='baseOffset'
                                label='Floorframe base offset (m)'
                                control={ control }
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className='form-fields-container'>
                        <Grid item xs={12} sm={2}>
                            <NumberField
                                name='emptyCenterOfGravity.x'
                                label='CoG X (m)'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <NumberField
                                name='emptyCenterOfGravity.y'
                                label='CoG Y (m)'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <NumberField
                                name='emptyCenterOfGravity.z'
                                label='CoG Z (m)'
                                control={ control }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className='form-fields-container'>
                        <Grid item xs={12} sm={4}>
                            <SelectField
                                name='linkedVessel'
                                label='Linked Barge'
                                control={ control }
                                options={GetVesselOptions(vessels)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <SelectMultipleField
                                name='contacts'
                                label='Contacts'
                                control={ control }
                                options={GetContactOptions(contacts.data)}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className='form-fields-container'>
                        <Grid item xs={12} sm={4}>
                            <CheckboxField
                                name='barge'
                                label='Barge'
                                control={ control }
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Divider/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{marginBottom: "10px"}}>Total weight & draught</Typography>
                    </Grid>
                    <Grid item xs={12} className='form-fields-container' sx={{ maxHeight: '25vh', overflowX: 'hidden', overflowY: 'auto' }}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{marginBottom: "10px"}}>Fresh Water</Typography>
                            <VesselWeightDraughtTable
                                name='totalWeightDraughtTable.fresh'
                                label='fresh water total weight draught table'
                                control={ control }
                                register = { register }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <Typography sx={{marginBottom: "10px"}}>Salt Water</Typography>
                            <VesselWeightDraughtTable
                                name='totalWeightDraughtTable.salt'
                                label='salt water total weight draught table'
                                control={ control }
                                register = { register }
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Divider/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{marginBottom: "10px"}}>Center of gravity height for draught</Typography>
                    </Grid>
                    <Grid item xs={12} className='form-fields-container' sx={{ maxHeight: '25vh', overflowX: 'hidden', overflowY: 'auto' }}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{marginBottom: "10px"}}>Secured</Typography>
                            <VesselDraughtKGMax
                                name='allowedCenterOfGravityHeightForDraughtTable.secured'
                                label='Secured center of gravity height'
                                control={ control }
                                register = { register }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <Typography sx={{marginBottom: "10px"}}>Unsecured</Typography>
                            <VesselDraughtKGMax
                                name='allowedCenterOfGravityHeightForDraughtTable.unsecured'
                                label='Unsecured center of gravity height'
                                control={ control }
                                register = { register }
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className='button-wrapper'>
                    <Grid container gap={1}>
                        <Button 
                            onClick={() => {dispatch(hideModal())}}
                            disabled={ isSubmitting }
                            variant='contained'
                        >
                            <CloseIcon fontSize="inherit" />&nbsp;Close
                        </Button>
                
                        {vessel && (
                            <Button
                                onClick={() => dispatch(removeVessel(vessel))}
                                disabled={isSubmitting}
                                variant="contained"
                                color="error"
                                sx={{ height: 45 }}
                            >
                                <DeleteIcon  fontSize="inherit" />
                                &nbsp;Delete
                            </Button>
                        )}
                    </Grid>
                    <Button 
                        type='submit'
                        disabled={ isSubmitting }
                        variant='contained'
                    >
                        <SaveIcon fontSize="inherit" />&nbsp;Save
                    </Button>
                </Grid>
            </Grid>
        </form>
    )
}

export default VesselForm;